import { useEffect, useState } from 'react';
import { objetivosService } from '../services/api';
import ModalLlaveAcceso from '../components/ModalLlaveAcceso';
import { useConfigNegocio } from '../hooks/useConfigNegocio';
import { parseCategorias } from '../constants/catalogos';

const ESTADOS = [
  { value: 'activo',     label: 'Activo',     bg: 'var(--ok-bg)', text: 'var(--ok)' },
  { value: 'completado', label: 'Completado', bg: 'var(--info-bg)', text: 'var(--info)' },
  { value: 'cancelado',  label: 'Cancelado',  bg: 'var(--brand-light)', text: 'var(--brand-texto)' },
];

function diasRestantes(fecha: string) {
  return Math.ceil((new Date(fecha).getTime() - Date.now()) / 86400000);
}
function progresoBarra(actual: number, meta: number) {
  return Math.min(100, Math.max(0, (actual / meta) * 100));
}

const formVacio = {
  titulo: '', descripcion: '', categoria: 'ventas',
  meta_valor: '', unidad: '$',
  fecha_inicio: new Date().toISOString().slice(0, 10),
  fecha_limite: '',
};

const S = {
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' } as React.CSSProperties,
  label: { fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 6 } as React.CSSProperties,
  input: { width: '100%', height: 40, border: '1px solid var(--line-strong)', borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' } as React.CSSProperties,
  chip: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 } as React.CSSProperties,
};

export default function ObjetivosSmart() {
  const config = useConfigNegocio();
  const CATEGORIAS = parseCategorias(config.objetivos_categorias);
  const [objetivos, setObjetivos]     = useState<any[]>([]);
  const [cargando, setCargando]       = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [modalAbierto, setModalAbierto]   = useState(false);
  const [editando, setEditando]       = useState<any>(null);
  const [form, setForm]               = useState(formVacio);
  const [guardando, setGuardando]     = useState(false);
  const [error, setError]             = useState('');

  // Llaves de acceso
  const [llaveAccion, setLlaveAccion] = useState<null | 'crear' | 'editar' | 'eliminar' | 'estado'>(null);
  const [pendienteEliminar, setPendienteEliminar] = useState<string | null>(null);
  const [pendienteEstado, setPendienteEstado]     = useState<{ obj: any; nuevoEstado: string } | null>(null);

  const refrescoMs = Math.max(10, parseInt(config.objetivos_refresco_seg, 10) || 60) * 1000;
  useEffect(() => {
    cargar();
    const interval = setInterval(cargar, refrescoMs);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refrescoMs]);

  const cargar = async () => {
    try {
      setSincronizando(true);
      // Sincronizar calcula automáticamente valor_actual y devuelve la lista actualizada
      const res = await objetivosService.sincronizar();
      setObjetivos(res.data);
    } catch {
      // Si falla la sincronización (p. ej. sin turno), cargar lista normal
      try {
        const res = await objetivosService.getAll();
        setObjetivos(res.data);
      } catch {}
    } finally {
      setCargando(false);
      setSincronizando(false);
    }
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm(formVacio);
    setError('');
    setLlaveAccion('crear');
  };

  const abrirEditar = (obj: any) => {
    setEditando(obj);
    setForm({
      titulo:       obj.titulo,
      descripcion:  obj.descripcion || '',
      categoria:    obj.categoria,
      meta_valor:   obj.meta_valor,
      unidad:       obj.unidad,
      fecha_inicio: obj.fecha_inicio?.slice(0, 10) || '',
      fecha_limite: obj.fecha_limite?.slice(0, 10) || '',
    });
    setError('');
    setLlaveAccion('editar');
  };

  const guardar = async () => {
    if (!form.titulo || !form.meta_valor || !form.fecha_limite) {
      setError('Título, meta y fecha límite son requeridos'); return;
    }
    setGuardando(true); setError('');
    try {
      if (editando) {
        await objetivosService.actualizar(editando.id, form);
      } else {
        await objetivosService.crear({ ...form, valor_actual: 0 });
      }
      setModalAbierto(false);
      setEditando(null);
      cargar();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const cambiarEstado = (obj: any, nuevoEstado: string) => {
    setPendienteEstado({ obj, nuevoEstado });
    setLlaveAccion('estado');
  };

  const confirmarCambioEstado = async () => {
    if (!pendienteEstado) return;
    await objetivosService.actualizar(pendienteEstado.obj.id, { estado: pendienteEstado.nuevoEstado });
    setPendienteEstado(null);
    cargar();
  };

  const eliminar = async (id: string) => {
    await objetivosService.eliminar(id);
    cargar();
  };

  const cat = (v: string) => CATEGORIAS.find(c => c.value === v) || { value: v, label: v, color: 'var(--muted)', sistema: false };
  const est = (v: string) => ESTADOS.find(e => e.value === v) || ESTADOS[0];

  const activos    = objetivos.filter(o => o.estado === 'activo');
  const completados = objetivos.filter(o => o.estado === 'completado');
  const cancelados  = objetivos.filter(o => o.estado === 'cancelado');

  const categoriaActual = CATEGORIAS.find(c => c.value === (editando?.categoria ?? form.categoria));

  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--ink)', margin: 0 }}>Objetivos SMART</h2>
          <p style={{ fontSize: 13, color: 'var(--faint)', margin: '2px 0 0' }}>
            Específicos · Medibles · Alcanzables · Relevantes · Temporales
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {sincronizando && (
            <span style={{ fontSize: 12, color: 'var(--info)' }}>Actualizando…</span>
          )}
          <button
            onClick={abrirNuevo}
            style={{ display: 'flex', alignItems: 'center', gap: 6, height: 40, padding: '0 18px', background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            🎯 Nuevo objetivo
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        <div style={{ background: 'var(--ok-bg)', border: '1px solid var(--ok-line)', borderRadius: 12, padding: '16px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: 30, fontWeight: 700, color: 'var(--ok)', margin: 0 }}>{activos.length}</p>
          <p style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 500, margin: '4px 0 0' }}>En progreso</p>
        </div>
        <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-line)', borderRadius: 12, padding: '16px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: 30, fontWeight: 700, color: 'var(--info)', margin: 0 }}>{completados.length}</p>
          <p style={{ fontSize: 12, color: 'var(--info)', fontWeight: 500, margin: '4px 0 0' }}>Completados</p>
        </div>
        <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: 30, fontWeight: 700, color: 'var(--ink-2)', margin: 0 }}>{cancelados.length}</p>
          <p style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, margin: '4px 0 0' }}>Cancelados</p>
        </div>
      </div>

      {cargando && <div style={{ color: 'var(--faint)', textAlign: 'center', padding: '48px 0' }}>Cargando…</div>}

      {/* Lista de objetivos */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {objetivos.map(obj => {
          const pct    = progresoBarra(parseFloat(obj.valor_actual), parseFloat(obj.meta_valor));
          const dias   = diasRestantes(obj.fecha_limite);
          const catObj = cat(obj.categoria);
          const estObj = est(obj.estado);
          const barColor = pct >= 100 ? '#22C55E' : pct >= 60 ? '#3B82F6' : pct >= 30 ? '#FBBF24' : '#F87171';

          return (
            <div key={obj.id} style={{ ...S.card, overflow: 'hidden' }}>
              {/* Barra de progreso */}
              <div style={{ height: 6, background: 'var(--well)' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: barColor, transition: 'width .3s' }} />
              </div>

              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ ...S.chip, background: catObj.color + '22', color: catObj.color }}>
                        {catObj.label}
                        {catObj.sistema && <span style={{ marginLeft: 4, opacity: .6 }}>· auto</span>}
                      </span>
                      <span style={{ ...S.chip, background: estObj.bg, color: estObj.text }}>
                        {estObj.label}
                      </span>
                      {obj.estado === 'activo' && (
                        <span style={{
                          ...S.chip,
                          background: dias < 0 ? 'var(--brand-light)' : dias <= 7 ? 'var(--warn-bg)' : 'var(--well)',
                          color:      dias < 0 ? 'var(--brand-texto)' : dias <= 7 ? 'var(--warn)' : 'var(--muted)',
                        }}>
                          {dias < 0 ? `Vencido hace ${Math.abs(dias)}d` : dias === 0 ? 'Vence hoy' : `${dias}d restantes`}
                        </span>
                      )}
                    </div>
                    <h3 style={{ fontWeight: 700, color: 'var(--ink-2)', fontSize: 16, lineHeight: 1.3, margin: 0 }}>{obj.titulo}</h3>
                    {obj.descripcion && <p style={{ fontSize: 13, color: 'var(--muted)', margin: '2px 0 0' }}>{obj.descripcion}</p>}
                  </div>

                  {/* Meta y progreso */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink-2)', margin: 0 }}>
                      {obj.unidad === '$' ? '$' : ''}{parseFloat(obj.valor_actual).toLocaleString('es-MX')}
                      {obj.unidad !== '$' ? ` ${obj.unidad}` : ''}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--faint)', margin: 0 }}>
                      de {obj.unidad === '$' ? '$' : ''}{parseFloat(obj.meta_valor).toLocaleString('es-MX')}{obj.unidad !== '$' ? ` ${obj.unidad}` : ''}
                    </p>
                    <p style={{ fontSize: 14, fontWeight: 700, margin: '2px 0 0', color: pct >= 100 ? 'var(--ok)' : pct >= 60 ? 'var(--info)' : 'var(--warn)' }}>
                      {pct.toFixed(0)}%
                    </p>
                  </div>
                </div>

                {/* Acciones — cambio de estado con contraseña */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line-2)' }}>
                  <p style={{ fontSize: 12, color: 'var(--faint)', flex: 1, margin: 0 }}>
                    {obj.usuario_nombre || '—'} · Límite: {obj.fecha_limite ? new Date(obj.fecha_limite).toLocaleDateString('es-MX') : '—'}
                  </p>
                  {obj.estado === 'activo' && (
                    <>
                      <button
                        onClick={() => cambiarEstado(obj, 'completado')}
                        style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px' }}
                      >
                        ✓ Completar
                      </button>
                      <button
                        onClick={() => cambiarEstado(obj, 'cancelado')}
                        style={{ fontSize: 12, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px' }}
                      >
                        ✕ Cancelar
                      </button>
                    </>
                  )}
                  {obj.estado !== 'activo' && (
                    <button
                      onClick={() => cambiarEstado(obj, 'activo')}
                      style={{ fontSize: 12, color: 'var(--info)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px' }}
                    >
                      Reactivar
                    </button>
                  )}
                  <button
                    onClick={() => abrirEditar(obj)}
                    style={{ fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px' }}
                  >Editar</button>
                  <button
                    onClick={() => { setPendienteEliminar(obj.id); setLlaveAccion('eliminar'); }}
                    style={{ fontSize: 12, color: '#FCA5A5', background: 'none', border: 'none', cursor: 'pointer' }}
                  >🗑</button>
                </div>
              </div>
            </div>
          );
        })}

        {!cargando && objetivos.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--muted)' }}>
            <p style={{ fontSize: 48, marginBottom: 16 }}>🎯</p>
            <p style={{ fontWeight: 600, color: 'var(--faint)', margin: 0 }}>Sin objetivos registrados</p>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: '4px 0 0' }}>Crea tu primer objetivo SMART para el restaurante</p>
          </div>
        )}
      </div>

      {/* Modal formulario (solo se abre tras pasar la llave) */}
      {modalAbierto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 16, boxShadow: '0 20px 50px rgba(0,0,0,.25)', width: '100%', maxWidth: 512, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 24px 8px', borderBottom: '1px solid var(--line-2)' }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-2)', margin: 0 }}>
                {editando ? 'Editar objetivo' : 'Nuevo objetivo SMART'}
              </h3>
              <p style={{ fontSize: 12, color: 'var(--faint)', margin: '2px 0 0' }}>Específico · Medible · Alcanzable · Relevante · Temporal</p>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Título */}
              <div>
                <label style={S.label}>S — ¿Qué se quiere lograr? *</label>
                <input
                  style={S.input}
                  placeholder="Ej. Incrementar ventas de fin de semana un 20%"
                  value={form.titulo}
                  onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                  onFocus={e => (e.target.style.borderColor = 'var(--brand-texto)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--line-strong)')}
                />
              </div>

              {/* Descripción */}
              <div>
                <label style={S.label}>Descripción adicional</label>
                <textarea
                  style={{ ...S.input, height: 'auto', padding: '8px 12px', resize: 'none' }}
                  rows={2}
                  placeholder="Contexto del objetivo…"
                  value={form.descripcion}
                  onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  onFocus={e => (e.target.style.borderColor = 'var(--brand-texto)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--line-strong)')}
                />
              </div>

              {/* Categoría */}
              <div>
                <label style={S.label}>Categoría</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CATEGORIAS.map(c => {
                    const activa = form.categoria === c.value;
                    return (
                      <button
                        key={c.value}
                        onClick={() => setForm(f => ({ ...f, categoria: c.value }))}
                        style={{
                          fontSize: 12, padding: '6px 12px', borderRadius: 999, fontWeight: 500, cursor: 'pointer',
                          background: activa ? c.color + '22' : 'var(--surface)',
                          color:      activa ? c.color : 'var(--muted)',
                          border:     activa ? '1px solid transparent' : '1px solid var(--line)',
                        }}
                      >
                        {c.label}
                        {c.sistema && <span style={{ marginLeft: 4, opacity: .5, fontSize: 10 }}>auto</span>}
                      </button>
                    );
                  })}
                </div>
                {categoriaActual?.sistema && (
                  <p style={{ fontSize: 12, color: 'var(--info)', margin: '6px 0 0' }}>
                    El progreso se actualiza automáticamente cada vez que abres esta página.
                  </p>
                )}
              </div>

              {/* M — Meta */}
              <div>
                <label style={S.label}>M — ¿Cómo se mide? (meta) *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    style={{ ...S.input, flex: 1 }}
                    placeholder="0"
                    value={form.meta_valor}
                    onChange={e => setForm(f => ({ ...f, meta_valor: e.target.value }))}
                    onFocus={e => (e.target.style.borderColor = 'var(--brand-texto)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--line-strong)')}
                  />
                  <input
                    style={{ ...S.input, width: 96, textAlign: 'center' }}
                    placeholder="Unidad"
                    value={form.unidad}
                    onChange={e => setForm(f => ({ ...f, unidad: e.target.value }))}
                    onFocus={e => (e.target.style.borderColor = 'var(--brand-texto)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--line-strong)')}
                  />
                </div>
                <p style={{ fontSize: 12, color: 'var(--faint)', margin: '4px 0 0' }}>Unidad: $, comandas, clientes, %, kg…</p>
              </div>

              {/* Fechas */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={S.label}>T — Fecha inicio</label>
                  <input
                    type="date"
                    style={S.input}
                    value={form.fecha_inicio}
                    onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))}
                    onFocus={e => (e.target.style.borderColor = 'var(--brand-texto)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--line-strong)')}
                  />
                </div>
                <div>
                  <label style={S.label}>T — Fecha límite *</label>
                  <input
                    type="date"
                    style={S.input}
                    value={form.fecha_limite}
                    onChange={e => setForm(f => ({ ...f, fecha_limite: e.target.value }))}
                    onFocus={e => (e.target.style.borderColor = 'var(--brand-texto)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--line-strong)')}
                  />
                </div>
              </div>

              {error && <p style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>{error}</p>}
            </div>

            <div style={{ padding: '0 24px 24px', display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setModalAbierto(false); setEditando(null); }}
                style={{ flex: 1, height: 42, border: '1px solid var(--line)', color: 'var(--muted)', background: 'var(--surface)', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
              >Cancelar</button>
              <button
                onClick={guardar}
                disabled={guardando}
                style={{ flex: 1, height: 42, background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? .4 : 1 }}
              >
                {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear objetivo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Llave — crear o editar */}
      {(llaveAccion === 'crear' || llaveAccion === 'editar') && (
        <ModalLlaveAcceso
          permiso="puede_gestionar_objetivos"
          titulo={llaveAccion === 'crear' ? 'Crear objetivo SMART' : 'Editar objetivo SMART'}
          descripcion="Esta acción requiere autorización de un usuario con permiso para gestionar objetivos."
          onSuccess={() => { setLlaveAccion(null); setModalAbierto(true); }}
          onCancel={() => setLlaveAccion(null)}
        />
      )}

      {/* Llave — cambiar estado */}
      {llaveAccion === 'estado' && pendienteEstado && (
        <ModalLlaveAcceso
          permiso="puede_gestionar_objetivos"
          titulo="Cambiar estado del objetivo"
          descripcion={`Marcar como "${pendienteEstado.nuevoEstado}": ${pendienteEstado.obj.titulo}`}
          onSuccess={() => { setLlaveAccion(null); confirmarCambioEstado(); }}
          onCancel={() => { setLlaveAccion(null); setPendienteEstado(null); }}
        />
      )}

      {/* Llave — eliminar */}
      {llaveAccion === 'eliminar' && pendienteEliminar && (
        <ModalLlaveAcceso
          permiso="puede_gestionar_objetivos"
          titulo="Eliminar objetivo"
          descripcion="Esta acción es irreversible y quedará registrada en auditoría."
          onSuccess={() => { const id = pendienteEliminar; setLlaveAccion(null); setPendienteEliminar(null); eliminar(id); }}
          onCancel={() => { setLlaveAccion(null); setPendienteEliminar(null); }}
        />
      )}
    </div>
  );
}
